import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { PageHero } from '@/components/shared/PageHero';
import { PROGRAMS } from '@/data/mockData';
import { formatDate, generateReference, storage } from '@/lib/storage';
import { Calendar, Clock, MapPin, Users, ArrowLeft, Check, Phone, FileCheck2 } from 'lucide-react';
import { toast } from 'sonner';

const ProgramDetail = () => {
  const { id } = useParams();
  const program = PROGRAMS.find((p) => p.id === id);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [open, setOpen] = useState(false);

  if (!program) return <Navigate to="/programs" replace />;

  const pct = Math.min(100, Math.round((program.registered / program.slots) * 100));
  const full = program.registered >= program.slots;

  const handleRegister = (e) => {
    e.preventDefault();
    if (!name.trim() || !contact.trim()) {
      toast.error('Please fill in your name and contact number.');
      return;
    }
    const ref = generateReference('REG');
    storage.push('programRegistrations', {
      id: ref,
      programId: program.id,
      programTitle: program.title,
      name: name.trim(),
      contact: contact.trim(),
      date: new Date().toISOString(),
      status: 'Submitted',
    });
    toast.success('Registration submitted!', { description: `Reference: ${ref}` });
    setName('');
    setContact('');
    setOpen(false);
  };

  return (
    <div data-testid="program-detail-page">
      <PageHero
        eyebrow={program.category}
        title={program.title}
        breadcrumbs={[{ label: 'Programs', to: '/programs' }, { label: 'Details' }]}
      />

      <section className="container-app section-padding">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Card className="overflow-hidden border-0 shadow-md-soft">
              <div className="aspect-[21/9] bg-secondary overflow-hidden">
                <img src={program.image} alt={program.title} className="h-full w-full object-cover" />
              </div>
              <div className="p-8">
                <h2 className="font-display text-2xl font-bold">About this program</h2>
                <p className="mt-3 text-foreground/80 leading-relaxed">{program.description}</p>

                <h3 className="mt-8 font-heading font-semibold">Requirements</h3>
                <p className="mt-2 text-sm text-foreground/80">{program.requirements}</p>

                <h3 className="mt-6 font-heading font-semibold">Contact</h3>
                <p className="mt-2 text-sm text-foreground/80 inline-flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> {program.contact}</p>

                <div className="mt-8">
                  <Button asChild variant="outline" data-testid="program-back-cta">
                    <Link to="/programs"><ArrowLeft className="mr-2 h-4 w-4" /> Back to programs</Link>
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          <aside className="space-y-5">
            <Card className="p-6 border-0 shadow-md-soft">
              <h3 className="font-heading font-semibold">Event Details</h3>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex gap-3"><Calendar className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" /><span><div className="font-medium">{formatDate(program.date)}</div><div className="text-muted-foreground">{program.time}</div></span></li>
                <li className="flex gap-3"><MapPin className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" /><span>{program.location}</span></li>
                <li className="flex gap-3"><Users className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" /><span>{program.registered} / {program.slots} registered</span></li>
              </ul>
              <Progress value={pct} className="mt-4 h-2" />
              <p className="mt-1 text-xs text-muted-foreground">{pct}% of slots filled</p>
            </Card>

            <Card className="p-6 border-0 bg-gradient-to-br from-primary/5 to-accent-soft">
              <FileCheck2 className="h-7 w-7 text-primary" />
              <h3 className="mt-3 font-heading font-semibold">Reserve a slot</h3>
              <p className="mt-1 text-xs text-muted-foreground">Walk-ins welcome but registering ahead helps the team prepare.</p>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button disabled={full} className="mt-4 w-full bg-gradient-primary text-primary-foreground" data-testid="program-register-trigger">
                    {full ? 'Slots filled' : 'Reserve free slot'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Reserve a slot · {program.title}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="reg-name">Full name</Label>
                      <Input id="reg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Juan dela Cruz" data-testid="program-reg-name" />
                    </div>
                    <div>
                      <Label htmlFor="reg-contact">Mobile number</Label>
                      <Input id="reg-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="0917-XXX-XXXX" data-testid="program-reg-contact" />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-gradient-primary text-primary-foreground" data-testid="program-reg-submit">
                        <Check className="mr-2 h-4 w-4" /> Submit
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              {full && <Badge className="mt-3 bg-destructive">Slots full</Badge>}
            </Card>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetail;
